import { Button, CircularProgress, Tooltip } from "@mui/material";
import { useIntl } from "react-intl";
import {
  ClaimCreditState,
  CourseModel,
  ResponseCode,
} from "services/facultyPortalClient";
import { useState } from "react";
import useGlobalContext from "globalProviders/GlobalDataProvider";
import CheckIcon from "@mui/icons-material/Check";
import LockIcon from "@mui/icons-material/Lock";
import { useQueryClient } from "react-query";

type ClaimCreditButtonProps = {
  course: CourseModel;
  alwaysShow?: boolean;
};

export default function ClaimCreditButton({ course, alwaysShow }: ClaimCreditButtonProps) {
  const { formatMessage: t } = useIntl();
  const { courseService } = useGlobalContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const queryClient = useQueryClient();

  const buttonState = isProcessing ? "Processing" : course.claimCreditState;

  if (buttonState === "Hidden" && !alwaysShow) {
    return null;
  }

  const handleClick = async () => {
    if (isProcessing || isFetching) return;

    setIsFetching(true);
    try {
      const response = await courseService.claimCredits(course.id);

      if (response.responseCode === ResponseCode.OK) {
        setTimeout(() => queryClient.invalidateQueries(["getCourses"]), 5000);
        setIsProcessing(true);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const getButtonContent = () => {
    switch (buttonState) {
      case ClaimCreditState.Claimed:
        return {
          text: t({ id: "creditClaimed" }),
          endIcon: <CheckIcon fontSize="small" />,
          variant: "success" as const,
          tooltip: t({ id: "alreadyClaimedCreditTooltip" }),
        };
      case ClaimCreditState.Disabled:
        return {
          text: t({ id: "claimCredit" }),
          endIcon: <LockIcon fontSize="small" />,
          variant: "outlined" as const,
          disabled: true,
          tooltip: t({ id: "eventNotEndedTooltip" }),
        };
      case ClaimCreditState.Active:
        return {
          text: t({ id: "claimCredit" }),
          variant: "contained" as const,
          tooltip: "",
        };
      case "Processing":
        return {
          text: t({ id: "processing" }),
          variant: "contained" as const,
          disabled: true,
          tooltip: "",
        };
      case ClaimCreditState.Hidden:
        return {
          text: t({ id: "claimCredit" }),
          endIcon: <LockIcon fontSize="small" />,
          variant: "outlined" as const,
          disabled: true,
          tooltip: "",
        };
      default:
        return {
          text: t({ id: "claimCredit" }),
          endIcon: <LockIcon fontSize="small" />,
          variant: "outlined" as const,
          disabled: true,
          tooltip: "",
        };
    }
  };

  const buttonConfig = getButtonContent();

  const button = (
    <Button
      startIcon={isFetching && <CircularProgress size={16} />}
      endIcon={buttonConfig.endIcon}
      variant={buttonConfig.variant}
      disabled={isFetching || buttonConfig.disabled}
      onClick={handleClick}
      size="small"
      fullWidth
    >
      {buttonConfig.text}
    </Button>
  );

  if (buttonConfig.tooltip) {
    return (
      <Tooltip title={buttonConfig.tooltip} arrow>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
}
